import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { ClerkProvider, useUser, useAuth } from "@clerk/clerk-react";
import { ensureFirebaseSignIn } from "./services/firebaseAuthBridge";
import { HelmetProvider } from "react-helmet-async";
import { db } from "./firebase";
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import "./clerkStyles.css"; // Import the Clerk styles
import * as Sentry from "@sentry/react";

// Environment detection - more robust check for production vs development
const isDevelopment = process.env.NODE_ENV === 'development' ||
                     window.location.hostname === 'localhost' ||
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.endsWith('-pickett2ks-projects.vercel.app') ||
                     window.location.hostname.includes('vercel-preview');

// Error monitoring — entirely inert unless REACT_APP_SENTRY_DSN is set, so this
// is a no-op by default and safe to ship. Set the DSN in Vercel to activate.
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || (isDevelopment ? "development" : "production"),
    tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
    sendDefaultPii: false, // don't auto-attach PII (this app handles form/submission data)
  });
}

// Use dev key on localhost, production key everywhere else
const PUBLISHABLE_KEY = isDevelopment
  ? (process.env.REACT_APP_CLERK_PUBLISHABLE_KEY_DEV || process.env.REACT_APP_CLERK_PUBLISHABLE_KEY)
  : process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

// ✅ This component ensures users are stored in Firebase when they sign up
const EnsureUserInFirestore = () => {
  const { user } = useUser();
  const { getToken } = useAuth();

  useEffect(() => {
    if (!user) return;

    const storeUser = async () => {
      const userRef = doc(db, "users", user.id);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // User data to store — new users default to Manager role
        // and are flagged for onboarding (company setup + first form wizard)
        const userData = {
          userId: user.id,
          email: user.primaryEmailAddress?.emailAddress || "",
          role: "Manager", // New signups are Managers of their own company
          isPremium: false, // New users start as non-premium
          proUser: false, // For backward compatibility
          freeFormRemaining: 1, // Allow one free form for new users
          needsOnboarding: true, // Triggers the onboarding wizard on first visit
          allowContactByTeam: true, // Assume consent; user can opt out in onboarding
          createdAt: new Date(),
          firstName: user.firstName || null,
          lastName: user.lastName || null
        };
        
        // ✅ Store in Firebase
        await setDoc(userRef, userData);
        
        // ✅ Send notification email about the new registration
        try {
          const response = await fetch('/api/email-handler', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              emailType: 'signup',
              userId: user.id,
              email: user.primaryEmailAddress?.emailAddress || "",
              firstName: user.firstName || "",
              lastName: user.lastName || "",
              createdAt: new Date().toISOString()
            }),
          });
          
          const result = await response.json();
          console.log("Signup notification result:", result);
        } catch (emailError) {
          // Log error but don't block the signup flow
          console.error("Failed to send signup notification email:", emailError);
        }
      } else {
        // Existing user: update lastLogin so desktop/mobile can track when they signed in
        try {
          await updateDoc(userRef, { lastLogin: serverTimestamp() });
        } catch (e) {
          // Non-critical; don't block app load
        }
      }
    };

    // After the user doc is ensured (so role/companyId claims are accurate),
    // sign in to Firebase Auth via the Clerk->Firebase bridge. Non-fatal while
    // rules are open; becomes the gate in Phase 5.
    storeUser().finally(() => { ensureFirebaseSignIn(() => getToken()); });
  }, [user, getToken]);

  return null;
};

// We need to define a routing component to provide navigation functions to Clerk
const ClerkWithRouting = () => {
  const navigate = useNavigate();
  
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      // Navigation configuration for React Router v7
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
            // Environment-specific configuration
      appearance={{
        baseTheme: undefined, // Use default theme
        elements: {
          // Fix modal positioning
          card: "mx-auto !important rounded-xl shadow-xl",
          modalBackdrop: "!items-center",
          modalContent: "!items-center !justify-center",
          rootBox: "!items-center !justify-center",
          // Ensure buttons match your app style
          formButtonPrimary: "bg-primary-600 hover:bg-primary-700",
          // Additional styling as needed
          formField: "rounded-md",
          formFieldInput: "rounded-md",
        },
        variables: {
          colorPrimary: '#3f51b5',
        }
      }}
    >
      <EnsureUserInFirestore />
      <App />
    </ClerkProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <BrowserRouter>
        <ClerkWithRouting />
      </BrowserRouter>
    </HelmetProvider>
  </React.StrictMode>
);
